// src/components/admin/AiMatchingToggle.tsx
//
// Admin on/off switch for the AI contract matcher (ai-match-contracts edge
// function). It is intentionally self-contained — it reads and writes ONLY the
// system_settings.ai_match_enabled column so it is unaffected by the other
// (partly phantom) fields in the bulk SettingsManagement form. The edge
// function treats this flag as the source of truth on every scan.
import { useEffect, useState } from 'react';
import { Sparkles, RefreshCw, AlertTriangle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';

export function AiMatchingToggle() {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [rowId, setRowId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data, error } = await supabase
          .from('system_settings')
          .select('id, ai_match_enabled')
          .limit(1)
          .maybeSingle();
        if (error) throw error;
        const row = data as { id?: string; ai_match_enabled?: boolean } | null;
        setRowId(row?.id ?? null);
        setEnabled(row?.ai_match_enabled ?? false);
      } catch (e) {
        console.error('Failed to load AI matching setting:', e);
        toast.error('Could not load the AI matching setting.');
        setEnabled(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggle = async () => {
    if (enabled === null || saving) return;
    const next = !enabled;
    setSaving(true);
    setEnabled(next); // optimistic
    try {
      let error;
      if (rowId) {
        ({ error } = await supabase
          .from('system_settings')
          .update({ ai_match_enabled: next, updated_at: new Date().toISOString() })
          .eq('id', rowId));
      } else {
        // No settings row yet — create one carrying the flag.
        const res = await supabase
          .from('system_settings')
          .insert([{ ai_match_enabled: next }])
          .select('id')
          .single();
        error = res.error;
        const inserted = res.data as { id?: string } | null;
        if (inserted?.id) setRowId(inserted.id);
      }
      if (error) throw error;
      toast.success(next ? 'AI matching turned ON' : 'AI matching turned OFF');
    } catch (e) {
      console.error('Failed to save AI matching setting:', e);
      setEnabled(!next); // revert
      toast.error('Could not update the AI matching setting.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-bw-surface shadow-card rounded-lg">
      <div className="px-6 py-4 border-b border-bw-border">
        <div className="flex items-center">
          <Sparkles className="h-5 w-5 text-bw-primary mr-2" />
          <div>
            <h3 className="text-lg font-medium text-bw-ink">AI Contract Matching</h3>
            <p className="text-sm text-bw-muted">
              Master switch for the "AI Best Matches" scan (Claude Haiku). When off, the scan
              button shows a "turned off" notice and spends nothing.
            </p>
          </div>
        </div>
      </div>

      <div className="px-6 py-5">
        {loading ? (
          <div className="flex items-center text-bw-muted text-sm">
            <RefreshCw className="h-4 w-4 animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-bw-ink">
                {enabled ? 'Enabled' : 'Disabled'}
              </p>
              <p className="text-sm text-bw-muted mt-0.5">
                Each scan costs Anthropic credits and is bounded by the daily spend cap
                (~$0.75/day before it asks a user to confirm more).
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={!!enabled}
              aria-label="Toggle AI contract matching"
              onClick={toggle}
              disabled={saving}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-bw-primary focus:ring-offset-2 disabled:opacity-60 ${
                enabled ? 'bg-bw-primary' : 'bg-gray-300'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-bw-surface shadow ring-0 transition duration-200 ease-in-out ${
                  enabled ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        )}

        {!loading && enabled && (
          <div className="mt-4 flex items-start gap-2 text-xs text-fit-moderate bg-fit-moderate-bg border border-fit-moderate-border rounded-md p-3">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              Matching is live — user scans will call Claude and incur spend. Also confirm
              the <code>ANTHROPIC_API_KEY</code> secret is set on the project, or scans will
              report a configuration error.
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
